import {
  MatchedBlockPair,
  NormalizedMarkdownBlock,
  NormalizedTableBlock,
} from "./markdownDiffTypes";
import { matchNormalizedTables } from "./markdownTableMatcher";

export const matchMarkdownBlocks = (
  previousBlocks: NormalizedMarkdownBlock[],
  currentBlocks: NormalizedMarkdownBlock[]
): MatchedBlockPair[] => {
  const matches = new Map<number, number>();
  const matchedPrevious = new Set<number>();
  const matchedCurrent = new Set<number>();

  const exactPairs = buildLcsPairs(
    previousBlocks,
    currentBlocks,
    (previousBlock, currentBlock) =>
      previousBlock.kind === currentBlock.kind &&
      previousBlock.exactSignature === currentBlock.exactSignature
  );
  applyPairs(exactPairs, matches, matchedPrevious, matchedCurrent);

  const previousTableEntries = previousBlocks
    .map((block, index) => ({ block, index }))
    .filter(
      ({ block, index }) =>
        block.kind === "table" && block.table && !matchedPrevious.has(index)
    );
  const currentTableEntries = currentBlocks
    .map((block, index) => ({ block, index }))
    .filter(
      ({ block, index }) =>
        block.kind === "table" && block.table && !matchedCurrent.has(index)
    );

  const tableMatches = matchNormalizedTables(
    previousTableEntries.map(
      ({ block }) => block.table as NormalizedTableBlock
    ),
    currentTableEntries.map(({ block }) => block.table as NormalizedTableBlock)
  );
  tableMatches.forEach((currentTableIndex, previousTableIndex) => {
    const previousBlockIndex = previousTableEntries[previousTableIndex].index;
    const currentBlockIndex = currentTableEntries[currentTableIndex].index;
    matches.set(previousBlockIndex, currentBlockIndex);
    matchedPrevious.add(previousBlockIndex);
    matchedCurrent.add(currentBlockIndex);
  });

  const previousTextEntries = previousBlocks
    .map((block, index) => ({ block, index }))
    .filter(
      ({ block, index }) =>
        isTextLikeBlock(block) && !matchedPrevious.has(index)
    );
  const currentTextEntries = currentBlocks
    .map((block, index) => ({ block, index }))
    .filter(
      ({ block, index }) => isTextLikeBlock(block) && !matchedCurrent.has(index)
    );

  const positionalTextPairCount = Math.min(
    previousTextEntries.length,
    currentTextEntries.length
  );

  previousTextEntries.slice(0, positionalTextPairCount).forEach((entry, pairIndex) => {
      const currentEntry = currentTextEntries[pairIndex];
      matches.set(entry.index, currentEntry.index);
      matchedPrevious.add(entry.index);
      matchedCurrent.add(currentEntry.index);
    });

  const normalizedMatches = normalizeCombinedMatches(matches);
  return buildOrderedPairs(
    previousBlocks,
    currentBlocks,
    normalizedMatches
  );
};

const normalizeCombinedMatches = (
  matches: Map<number, number>
): Map<number, number> => {
  const normalizedMatches = new Map<number, number>();
  let lastCurrentIndex = -1;

  Array.from(matches.entries())
    .sort(([previousIndexA], [previousIndexB]) => previousIndexA - previousIndexB)
    .forEach(([previousIndex, currentIndex]) => {
      if (currentIndex <= lastCurrentIndex) {
        return;
      }

      normalizedMatches.set(previousIndex, currentIndex);
      lastCurrentIndex = currentIndex;
    });

  return normalizedMatches;
};

const isTextLikeBlock = (block: NormalizedMarkdownBlock): boolean =>
  block.kind !== "table" && block.kind !== "image" && block.kind !== "link";

const applyPairs = (
  pairs: Array<{ previousIndex: number; currentIndex: number }>,
  matches: Map<number, number>,
  matchedPrevious: Set<number>,
  matchedCurrent: Set<number>
) => {
  pairs.forEach(({ previousIndex, currentIndex }) => {
    if (
      matchedPrevious.has(previousIndex) ||
      matchedCurrent.has(currentIndex)
    ) {
      return;
    }

    matches.set(previousIndex, currentIndex);
    matchedPrevious.add(previousIndex);
    matchedCurrent.add(currentIndex);
  });
};

const buildOrderedPairs = (
  previousBlocks: NormalizedMarkdownBlock[],
  currentBlocks: NormalizedMarkdownBlock[],
  matches: Map<number, number>
): MatchedBlockPair[] => {
  const currentToPrevious = new Map<number, number>();
  matches.forEach((currentIndex, previousIndex) => {
    currentToPrevious.set(currentIndex, previousIndex);
  });

  const pairs: MatchedBlockPair[] = [];
  let previousIndex = 0;
  let currentIndex = 0;

  while (
    previousIndex < previousBlocks.length ||
    currentIndex < currentBlocks.length
  ) {
    if (previousIndex >= previousBlocks.length) {
      pairs.push({
        previousBlock: null,
        currentBlock: currentBlocks[currentIndex],
      });
      currentIndex += 1;
      continue;
    }

    if (currentIndex >= currentBlocks.length) {
      pairs.push({
        previousBlock: previousBlocks[previousIndex],
        currentBlock: null,
      });
      previousIndex += 1;
      continue;
    }

    const previousMatch = matches.get(previousIndex);
    const currentMatch = currentToPrevious.get(currentIndex);

    if (
      previousMatch === currentIndex
    ) {
      pairs.push({
        previousBlock: previousBlocks[previousIndex],
        currentBlock: currentBlocks[currentIndex],
      });
      previousIndex += 1;
      currentIndex += 1;
      continue;
    }

    if (
      currentMatch === undefined
    ) {
      pairs.push({
        previousBlock: null,
        currentBlock: currentBlocks[currentIndex],
      });
      currentIndex += 1;
      continue;
    }

    if (
      currentMatch !== undefined &&
      currentMatch > previousIndex
    ) {
      pairs.push({
        previousBlock: previousBlocks[previousIndex],
        currentBlock: null,
      });
      previousIndex += 1;
      continue;
    }

    pairs.push({
      previousBlock: previousBlocks[previousIndex],
      currentBlock: null,
    });
    previousIndex += 1;
  }

  return pairs;
};

const buildLcsPairs = (
  previousBlocks: NormalizedMarkdownBlock[],
  currentBlocks: NormalizedMarkdownBlock[],
  isMatch: (
    previousBlock: NormalizedMarkdownBlock,
    currentBlock: NormalizedMarkdownBlock
  ) => boolean
): Array<{ previousIndex: number; currentIndex: number }> => {
  const lcs = Array.from({ length: previousBlocks.length + 1 }, () =>
    Array(currentBlocks.length + 1).fill(0)
  );

  for (
    let previousIndex = previousBlocks.length - 1;
    previousIndex >= 0;
    previousIndex -= 1
  ) {
    for (
      let currentIndex = currentBlocks.length - 1;
      currentIndex >= 0;
      currentIndex -= 1
    ) {
      if (isMatch(previousBlocks[previousIndex], currentBlocks[currentIndex])) {
        lcs[previousIndex][currentIndex] =
          lcs[previousIndex + 1][currentIndex + 1] + 1;
      } else {
        lcs[previousIndex][currentIndex] = Math.max(
          lcs[previousIndex + 1][currentIndex],
          lcs[previousIndex][currentIndex + 1]
        );
      }
    }
  }

  const pairs: Array<{ previousIndex: number; currentIndex: number }> = [];
  let previousIndex = 0;
  let currentIndex = 0;

  while (
    previousIndex < previousBlocks.length &&
    currentIndex < currentBlocks.length
  ) {
    if (isMatch(previousBlocks[previousIndex], currentBlocks[currentIndex])) {
      pairs.push({ previousIndex, currentIndex });
      previousIndex += 1;
      currentIndex += 1;
      continue;
    }

    if (
      lcs[previousIndex + 1][currentIndex] >=
      lcs[previousIndex][currentIndex + 1]
    ) {
      previousIndex += 1;
    } else {
      currentIndex += 1;
    }
  }

  return pairs;
};
